// src/components/reports/CandidateWorkStatusReport.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Badge } from 'react-bootstrap';
import TitleWithBack from '../common/TitleWithBack';
import PaginatedTable from '../PaginatedTable';

// Accountability report: for every candidate an examiner has taken action
// on (submitted work, confirmed an empty folder, or marked absent), plus
// candidates added under protest - filterable per series, exam centre, and
// module. Shared between the Admin and Archiver interfaces.
export default function CandidateWorkStatusReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [category, setCategory] = useState('ALL'); // ALL | SUBMITTED | EMPTY | ABSENT | UNDER_PROTEST
  const [filters, setFilters] = useState({
    seriesName: '',
    examCentreName: '',
    examCentreNumber: '',
    moduleName: '',
    moduleCode: ''
  });

  useEffect(() => {
    setLoading(true);
    axios
      .get('/api/reports/candidate-work-status')
      .then((res) => {
        setRows(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching candidate work status report.');
        setLoading(false);
      });
  }, []);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const filteredRows = rows.filter((r) => {
    if (category === 'UNDER_PROTEST' && !r.underProtest) return false;
    if (category !== 'ALL' && category !== 'UNDER_PROTEST' && r.status !== category) return false;
    if (filters.seriesName && !(r.seriesName || '').toLowerCase().includes(filters.seriesName.toLowerCase())) return false;
    if (filters.examCentreName && !(r.examCentreName || '').toLowerCase().includes(filters.examCentreName.toLowerCase())) return false;
    if (filters.examCentreNumber && !(r.examCentreNumber || '').toLowerCase().includes(filters.examCentreNumber.toLowerCase())) return false;
    if (filters.moduleName && !(r.moduleName || '').toLowerCase().includes(filters.moduleName.toLowerCase())) return false;
    if (filters.moduleCode && !(r.moduleCode || '').toLowerCase().includes(filters.moduleCode.toLowerCase())) return false;
    return true;
  });

  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    if (r.underProtest) acc.UNDER_PROTEST = (acc.UNDER_PROTEST || 0) + 1;
    return acc;
  }, {});

  const statusBadge = (status) => {
    const variant = status === 'SUBMITTED' ? 'success' : status === 'EMPTY' ? 'warning' : 'secondary';
    const label = status === 'SUBMITTED' ? 'Has Work' : status === 'EMPTY' ? 'Empty (confirmed)' : 'Absent';
    return <Badge bg={variant} text={status === 'EMPTY' ? 'dark' : undefined}>{label}</Badge>;
  };

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'registrationNumber', header: 'Reg No.' },
    { key: 'studentName', header: 'Student Name' },
    { key: 'protest', header: 'Roster' },
    { key: 'seriesName', header: 'Series' },
    { key: 'examCentre', header: 'Exam Centre' },
    { key: 'module', header: 'Module' },
    { key: 'status', header: 'Status' },
    { key: 'reason', header: 'Reason / Comment' },
    { key: 'recordedBy', header: 'Recorded By' },
    { key: 'recordedDate', header: 'Recorded Date' },
  ];

  const renderRow = (r, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td><small>{r.registrationNumber}</small></td>
      <td><small>{r.studentName}</small></td>
      <td>
        {r.underProtest ? <Badge bg="danger">Under Protest</Badge> : <small className="text-muted">Roster</small>}
      </td>
      <td><small>{r.seriesName}</small></td>
      <td><small>{r.examCentreNumber} - {r.examCentreName}</small></td>
      <td><small>{r.moduleCode} - {r.moduleName}</small></td>
      <td>{statusBadge(r.status)}</td>
      <td><small>{r.reason || '-'}</small></td>
      <td><small>{r.recordedBy || '-'}</small></td>
      <td><small>{r.recordedDate ? new Date(r.recordedDate).toLocaleString() : '-'}</small></td>
    </>
  );

  if (loading) return <div className="mt-4 container">Loading...</div>;
  if (error) return <div className="mt-4 container text-danger">{error}</div>;

  return (
    <Container className="mt-2" fluid>
      <TitleWithBack title="Candidate Work Status Report" />

      <Row className="mb-3">
        <Col md={2}>
          <Form.Label>Category</Form.Label>
          <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="ALL">All ({rows.length})</option>
            <option value="SUBMITTED">Has Work ({counts.SUBMITTED || 0})</option>
            <option value="EMPTY">Empty - confirmed ({counts.EMPTY || 0})</option>
            <option value="ABSENT">Absent ({counts.ABSENT || 0})</option>
            <option value="UNDER_PROTEST">Sat Under Protest ({counts.UNDER_PROTEST || 0})</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label>Series</Form.Label>
          <Form.Control type="text" name="seriesName" value={filters.seriesName} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Centre No</Form.Label>
          <Form.Control type="text" name="examCentreNumber" value={filters.examCentreNumber} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Centre Name</Form.Label>
          <Form.Control type="text" name="examCentreName" value={filters.examCentreName} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Module Code</Form.Label>
          <Form.Control type="text" name="moduleCode" value={filters.moduleCode} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Module Name</Form.Label>
          <Form.Control type="text" name="moduleName" value={filters.moduleName} onChange={handleFilterChange} />
        </Col>
      </Row>

      {filteredRows.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={filteredRows}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
    </Container>
  );
}
